import { useState } from "react";
import ExcelJS from "exceljs";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import {
  COMPLAINT_EXPORT_COLUMNS,
  COMPLAINT_HISTORY_COLUMNS,
  getExportDateTime,
  getHistoryForRow,
  loadAllComplaintHistory,
  mapComplaintRowsForExport,
  mapHistoryRowsForExport,
} from "./ComplaintExportHelpers";
import { useComplaintStore } from "./ComplaintStore/useComplaintStore";

const HEADER_FILL = {
  type: "pattern",
  pattern: "solid",
  fgColor: { argb: "003366" },
};

const HISTORY_HEADER_FILL = {
  type: "pattern",
  pattern: "solid",
  fgColor: { argb: "003366" },
};

const MAIN_HEAD_STYLES = {
  fillColor: [0, 51, 102],
  textColor: [255, 255, 255],
  fontSize: 7,
  halign: "center",
};

const MAIN_BODY_STYLES = {
  fontSize: 6.5,
  halign: "center",
};

const HISTORY_HEAD_STYLES = {
  fillColor: [0, 51, 102],
  textColor: [255, 255, 255],
  fontSize: 7,
  halign: "center",
};

const HISTORY_BODY_STYLES = {
  fontSize: 6.5,
  halign: "center",
};

const centerCell = (cell) => {
  cell.alignment = { horizontal: "center" };
};

const styleHeaderRow = (row, fill, columnCount) => {
  const boldFont = { bold: true, color: { argb: "FFFFFFFF" } };
  row.eachCell({ includeEmpty: true }, (cell, colNumber) => {
    cell.font = boldFont;
    centerCell(cell);
    if (colNumber <= columnCount) {
      cell.fill = fill;
    }
  });
};

// Explicit widths (mm) for COMPLAINT_EXPORT_COLUMNS / mainRows columns, sized to fit
// an A3 landscape page with room for each column's full header text on one or two
// lines — narrower widths force autoTable into vertical letter-by-letter wrapping.
const MAIN_COLUMN_STYLES = {
  0: { cellWidth: 10 }, // S.No
  1: { cellWidth: 18 }, // CG No
  2: { cellWidth: 22 }, // Complainant Name
  3: { cellWidth: 16 }, // Contact No
  4: { cellWidth: 18 }, // Complaint Type
  5: { cellWidth: 20 }, // Complaint Category
  6: { cellWidth: 32 }, // Description
  7: { cellWidth: 16 }, // Zone
  8: { cellWidth: 14 }, // Ward No
  9: { cellWidth: 24 }, // Street Location
  10: { cellWidth: 20 }, // Complaint Created On
  11: { cellWidth: 20 }, // Complaint Closure On
  12: { cellWidth: 16 }, // Mode Of Reporting
  13: { cellWidth: 18 }, // Predefined SLA Duration (Min)
  14: { cellWidth: 18 }, // Actual Duration (Hr:Min)
  15: { cellWidth: 15 }, // Status
  16: { cellWidth: 22 }, // Close Time Location
  17: { cellWidth: 28 }, // Action Remarks
  18: { cellWidth: 18 }, // Revised Ward No
  19: { cellWidth: 24 }, // Revised Street Location
};

// Explicit widths (mm) for COMPLAINT_HISTORY_COLUMNS, sized to fit the indented
// history sub-table on an A3 landscape page.
const HISTORY_COLUMN_STYLES = {
  0: { cellWidth: 12 }, // S.No
  1: { cellWidth: 40 }, // Assigned To
  2: { cellWidth: 22 }, // Status
  3: { cellWidth: 70 }, // Action Remarks
  4: { cellWidth: 35 }, // Assigned On
  5: { cellWidth: 35 }, // Action Taken On
  6: { cellWidth: 28 }, // Total Time (Hr:Min)
  7: { cellWidth: 35 }, // Assigned By
};

const getStartY = (doc) => doc.lastAutoTable?.finalY ?? 35;

const addPageIfNeeded = (doc, startY, minSpace = 30) => {
  const pageHeight = doc.internal.pageSize.height;
  if (startY + minSpace > pageHeight - 14) {
    doc.addPage();
    return 20;
  }
  return startY;
};

export const ExportComplaint = ({ complaintData = [] }) => {
  const fetchComplaintAssignedDetails = useComplaintStore(
    (state) => state.fetchComplaintAssignedDetails
  );
  const [isExportingExcel, setIsExportingExcel] = useState(false);
  const [isExportingPdf, setIsExportingPdf] = useState(false);

  const loadAllHistory = (data) =>
    loadAllComplaintHistory(data, fetchComplaintAssignedDetails);

  const exportToExcel = async () => {
    setIsExportingExcel(true);

    try {
      const historyById = await loadAllHistory(complaintData);
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet("Complaint List");
      const boldFont = { bold: true };
      const mainColumnCount = COMPLAINT_EXPORT_COLUMNS.length;
      const historyColumnCount = COMPLAINT_HISTORY_COLUMNS.length;

      const downloadDateTime = getExportDateTime();
      worksheet.addRow(["", "", downloadDateTime, ""]);
      worksheet.mergeCells(1, 2, 1, mainColumnCount);
      worksheet.getCell(1, 2).value = downloadDateTime;
      worksheet.getCell(1, 2).font = boldFont;
      worksheet.getCell(1, 2).alignment = { horizontal: "center" };

      const mainHeaderRow = worksheet.addRow(COMPLAINT_EXPORT_COLUMNS);
      styleHeaderRow(mainHeaderRow, HEADER_FILL, mainColumnCount);

      const mainRows = mapComplaintRowsForExport(complaintData);
      mainRows.forEach((mainRow, index) => {
        const complaint = complaintData[index];
        const history = getHistoryForRow(complaint, historyById);

        worksheet.addRow(mainRow).eachCell({ includeEmpty: true }, centerCell);

        if (!history.length) return;

        const historyTitleRow = worksheet.addRow([
          "",
          `Assignment History - ${complaint.cgNo || "N/A"}`,
        ]);
        worksheet.mergeCells(
          historyTitleRow.number,
          2,
          historyTitleRow.number,
          1 + historyColumnCount
        );
        const titleCell = historyTitleRow.getCell(2);
        titleCell.font = boldFont;
        titleCell.fill = HISTORY_HEADER_FILL;

        const historyHeaderRow = worksheet.addRow(["", ...COMPLAINT_HISTORY_COLUMNS]);
        styleHeaderRow(historyHeaderRow, HISTORY_HEADER_FILL, historyColumnCount + 1);

        mapHistoryRowsForExport(history).forEach((historyRow) => {
          worksheet
            .addRow(["", ...historyRow])
            .eachCell({ includeEmpty: true }, centerCell);
        });

        worksheet.addRow([]);
      });

      worksheet.columns = [
        { width: 6 }, // S.No
        { width: 16 }, // CG No
        { width: 20 }, // Complainant Name
        { width: 14 }, // Contact No
        { width: 16 }, // Complaint Type
        { width: 18 }, // Complaint Category
        { width: 40 }, // Description
        { width: 14 }, // Zone
        { width: 12 }, // Ward No
        { width: 22 }, // Street Location
        { width: 20 }, // Complaint Created On
        { width: 20 }, // Complaint Closure On
        { width: 16 }, // Mode Of Reporting
        { width: 18 }, // Predefined SLA Duration (Min)
        { width: 18 }, // Actual Duration (Hr:Min)
        { width: 12 }, // Status
        { width: 20 }, // Close Time Location
        { width: 40 }, // Action Remarks
        { width: 16 }, // Revised Ward No
        { width: 22 }, // Revised Street Location
      ];

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "ComplaintList.xlsx";
      a.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error generating Excel file:", error);
    } finally {
      setIsExportingExcel(false);
    }
  };

  const exportToPDF = async () => {
    setIsExportingPdf(true);

    try {
      const historyById = await loadAllHistory(complaintData);
      const mainRows = mapComplaintRowsForExport(complaintData);
      const doc = new jsPDF({ orientation: "landscape", format: "a3" });
      const pageWidth = doc.internal.pageSize.width;

      doc.setFont("helvetica", "bold");
      doc.setFontSize(16);
      doc.text("Complaint List", pageWidth / 2, 20, { align: "center" });
      doc.setFontSize(10);
      doc.text(getExportDateTime(), pageWidth / 2, 28, { align: "center" });

      let startY = 35;

      mainRows.forEach((mainRow, index) => {
        const complaint = complaintData[index];
        const history = getHistoryForRow(complaint, historyById);

        startY = addPageIfNeeded(doc, startY, 12);

        autoTable(doc, {
          startY,
          head: index === 0 ? [COMPLAINT_EXPORT_COLUMNS] : [],
          body: [mainRow],
          headStyles: MAIN_HEAD_STYLES,
          bodyStyles: MAIN_BODY_STYLES,
          columnStyles: MAIN_COLUMN_STYLES,
          styles: {
            fontSize: 7,
            cellPadding: 2,
            overflow: "linebreak",
            valign: "middle",
          },
          margin: { left: 6, right: 6 },
        });

        startY = getStartY(doc) + 4;

        if (!history.length) return;

        startY = addPageIfNeeded(doc, startY, 24);

        doc.setFont("helvetica", "bold");
        doc.setFontSize(9);
        doc.setFillColor(219, 234, 254);
        doc.rect(6, startY - 5, pageWidth - 12, 8, "F");
        doc.setTextColor(0, 51, 102);
        doc.text(`Assignment History - ${complaint.cgNo || "N/A"}`, 10, startY);
        doc.setTextColor(0, 0, 0);

        autoTable(doc, {
          startY: startY + 3,
          head: [COMPLAINT_HISTORY_COLUMNS],
          body: mapHistoryRowsForExport(history),
          headStyles: HISTORY_HEAD_STYLES,
          bodyStyles: HISTORY_BODY_STYLES,
          columnStyles: HISTORY_COLUMN_STYLES,
          styles: {
            fontSize: 7,
            cellPadding: 2,
            overflow: "linebreak",
            valign: "middle",
          },
          margin: { left: 14, right: 6 },
        });

        startY = getStartY(doc) + 8;
      });

      doc.save("ComplaintList.pdf");
    } catch (error) {
      console.error("Error generating PDF file:", error);
    } finally {
      setIsExportingPdf(false);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={exportToExcel}
        className="button-container ms-3"
        disabled={isExportingExcel || isExportingPdf}
      >
        <i className="fa-solid fa-file-excel me-1 icon-container"></i>
        {isExportingExcel ? "Exporting..." : "Excel"}
      </button>
      <button
        type="button"
        onClick={exportToPDF}
        className="button-container ms-3"
        disabled={isExportingExcel || isExportingPdf}
      >
        <i className="fa-solid fa-file-pdf me-1 icon-container"></i>
        {isExportingPdf ? "" : "PDF"}
      </button>
    </>
  );
};
